import { StatusCode } from "@/core/api/enums/status_code"
import { RESTSettings } from "@/core/api/client/rest_settings"
import { JsonUtil } from "@/core/api/utils/json_util"
import { ResponseModel } from "@/core/api/models/response_model"
import { RequestModel } from "@/core/api/models/request_model"
import { APIFactory } from "../factories/api_factory"
import { HTTPMethod } from "../enums/http_method"
import { Environment } from "@/core/api/constants/environment"
import { COOKIE_AUTH_ENABLED, getCsrfHeaders } from "@/core/api/auth_cookies"

export class RESTClient {
  private _settings: RESTSettings

  constructor(settings: RESTSettings) {
    this._settings = settings
  }

  private _sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  private async _fetch(
    token: string,
    url: string,
    options: RequestInit = {},
    numRetries: number = 1,
  ): Promise<Response> {
    let attempt: number = 0
    let lastError: any
    const domainUrl: string = this.getUrl(url)

    if (typeof options?.body === "string") {
      options.headers = {
        ...options.headers,
        "Content-Type": "application/json",
      }
    }

    // Safely convert header keys and values to strings
    // const headers: Record<string, string> = {
    //   Authorization: `Bearer ${token}`,
    // }

    // if (options?.headers && typeof options.headers === "object") {
    //   for (const [key, value] of Object.entries(options.headers)) {
    //     headers[String(key)] = String(value)
    //   }
    // }

    // options = {
    //   ...options,
    //   headers: { ...headers },
    // }
    const headers: Record<string, string> = {
      ...getCsrfHeaders(),
    }

    if (!COOKIE_AUTH_ENABLED && token) {
      headers.Authorization = `Bearer ${token}`
    }

    if (options?.headers && typeof options.headers === "object") {
      for (const [key, value] of Object.entries(options.headers)) {
        headers[String(key)] = String(value)
      }
    }

    options = {
      ...options,
      credentials: COOKIE_AUTH_ENABLED ? "include" : options.credentials,
      headers: { ...headers },
    }

    if (this._settings.numRetries > numRetries) {
      numRetries = this._settings.numRetries
    }

    while (attempt < numRetries) {
      try {
        const response: Response = await fetch(domainUrl, options)
        if (response.ok) {
          return response
        } else {
          throw new Error(`RESTClient - HTTP error! status: ${response.status}`)
        }
      } catch (error) {
        lastError = error
        attempt++

        if (attempt < this._settings.numRetries) {
          const delayTime: number = this._settings.backoff * 2 ** (attempt - 1)
          console.warn(
            `RESTClient - Attempt ${attempt} failed. Retrying in ${delayTime}ms...`,
          )
          await this._sleep(delayTime)
        } else {
          console.error(
            `RESTClient - All ${this._settings.numRetries} attempts failed.`,
          )
          throw lastError
        }
      }
    }

    throw lastError
  }

  protected async fetchWithRetry(
    token: string,
    url: string,
    options: RequestInit = {},
    numRetries: number = 1,
  ): Promise<any> {
    try {
      const response: Response = await this._fetch(
        token,
        url,
        options,
        numRetries,
      )
      const contentType: string | null = response.headers.get("content-type")
      if (response.status !== StatusCode.NoContent) {
        if (
          contentType &&
          contentType.toLowerCase().indexOf("application/json") !== -1
        ) {
          return await response.json()
        } else {
          return await response.text()
        }
      }

      return response
    } catch (error) {
      const requestUrl: string = `${this._settings.domainUrl}${url}`
      console.error(`RESTClient - Url: ${requestUrl}, Token: ${token}`, {
        options: options,
        error,
      })
      return null
    }
  }

  public async getWithParams<T>(
    token: string,
    url: string,
    model: T,
  ): Promise<ResponseModel> {
    const params: string = JsonUtil.serializeJson64(model)
    url += `?params=${params}`
    const numRetries: number = 3
    const res: ResponseModel = await this.fetchWithRetry(
      token,
      url,
      {},
      numRetries,
    )
    return res
  }

  public async get<T>(token: string, url: string): Promise<ResponseModel> {
    const numRetries: number = 3
    return this.fetchWithRetry(token, url, {}, numRetries)
  }

  public async post<T>(
    token: string,
    url: string,
    model: T,
  ): Promise<ResponseModel> {
    const options: RequestInit = this.createRequestOptions<T>(
      model,
      HTTPMethod.POST,
    )
    const response: ResponseModel = await this.fetchWithRetry(
      token,
      url,
      options,
    )

    return response
  }

  public async put<T>(
    token: string,
    url: string,
    model: T,
  ): Promise<ResponseModel> {
    const options: RequestInit = this.createRequestOptions<T>(
      model,
      HTTPMethod.PUT,
    )
    const response: ResponseModel = await this.fetchWithRetry(
      token,
      url,
      options,
    )

    return response
  }

  public async patch<T>(
    token: string,
    url: string,
    model: T,
  ): Promise<ResponseModel> {
    const options: RequestInit = this.createRequestOptions<T>(
      model,
      HTTPMethod.PATCH,
    )
    const response: ResponseModel = await this.fetchWithRetry(
      token,
      url,
      options,
    )

    return response
  }

  public async delete<T>(
    token: string,
    url: string,
    model: T,
  ): Promise<ResponseModel> {
    const options: RequestInit = this.createRequestOptions<T>(
      model,
      HTTPMethod.DELETE,
    )
    const response: ResponseModel = await this.fetchWithRetry(
      token,
      url,
      options,
    )

    return response
  }

  private createRequestOptions<T>(model: T, method: string): RequestInit {
    const request: RequestModel = APIFactory.createRequest(model)

    const headers: Record<string, string> = {
      Accept: "application/json",
    }

    const jsonData = JSON.stringify(request)
    const options: RequestInit = {
      method: method,
      headers: headers,
      body: jsonData,
    }

    return options
  }

  private getUrl(url: string): string {
    if (!url.startsWith("/")) url = "/" + url

    if (url.startsWith("/nxt/")) {
      if (process.env.NEXT_PUBLIC_API_NXT_URL) {
        url = `${process.env.NEXT_PUBLIC_API_NXT_URL}${url}`
      } else {
        url = `https://apistaging-nxt.flextribe.io${url}`
        // url = `https://dev-api-nxt.flextribe.io${url}`
      }

      // if (process.env.NODE_ENV === Environment.Production) {
      //   url = `https://api-nxt.flextribe.io${url}`
      // }
      // if (process.env.NODE_ENV === Environment.Staging) {
      //   url = `https://apistaging-nxt.flextribe.io${url}`
      // }
      // if (process.env.NODE_ENV === Environment.Development) {
      //   if (process.env.NEXT_PUBLIC_API_NXT_URL) {
      //     url = `${process.env.NEXT_PUBLIC_API_NXT_URL}${url}`
      //   } else {
      //     url = `https://apistaging-nxt.flextribe.io${url}`
      //     // url = `https://dev-api-nxt.flextribe.io${url}`
      //   }
      // }
      // if (process.env.NODE_ENV === Environment.LocalHost) {
      //   if (process.env.NEXT_PUBLIC_API_NXT_URL) {
      //     url = `${process.env.NEXT_PUBLIC_API_NXT_URL}${url}`
      //   } else {
      //     url = `https://apistaging-nxt.flextribe.io${url}`
      //   }
      // }
    } else {
      url = `${this._settings.domainUrl}${url}`
    }

    return url
  }
}
